import { css, SerializedStyles } from '@emotion/core'

import { Color } from '../../../enums/color'
import { ButtonColor, Variant } from '../types'
import {
  filledBg,
  filledHoverBg,
  filledText,
  onColorBg,
  onColorHoverBg,
  onColorText,
  subtleText,
  tonalBg,
  tonalHoverBg,
  tonalText,
} from './colorTokens'

const getFilledStyle = (color: ButtonColor): SerializedStyles => css`
  background-color: ${filledBg[color]};
  color: ${filledText[color]};

  &:not(:disabled) {
    &:hover {
      background-color: ${filledHoverBg[color]};
    }
  }
`

const getTonalStyle = (color: ButtonColor): SerializedStyles => css`
  background-color: ${tonalBg[color]};
  color: ${tonalText[color]};

  &:not(:disabled) {
    &:hover {
      background-color: ${tonalHoverBg[color]};
      ${color === 'danger' && `color: ${Color.Red800};`}
    }
  }
`

const getOutlinedStyle = (color: ButtonColor): SerializedStyles => css`
  background-color: transparent;
  color: ${subtleText[color]};
  box-shadow: inset 0 0 0 1px ${Color.Grey200};

  &:not(:disabled) {
    &:hover {
      background-color: ${Color.Grey100};
    }
  }
`

const getTextStyle = (color: ButtonColor): SerializedStyles => css`
  background-color: transparent;
  color: ${subtleText[color]};

  &:not(:disabled) {
    &:hover {
      background-color: ${Color.Grey100};
    }
  }
`

const getOnColorStyle = (color: ButtonColor): SerializedStyles => css`
  background-color: ${onColorBg[color]};
  color: ${onColorText[color]};

  &:not(:disabled) {
    &:hover {
      background-color: ${onColorHoverBg[color]};
    }
  }
`

// `textOnColor` is the text variant rendered on a colored surface: white label
// on a transparent background, with subtle white overlays for hover/pressed.
// The accent color is excluded by design (matches the Figma component note);
// in that case we fall back to the regular text styling.
const getTextOnColorStyle = (color: ButtonColor): SerializedStyles => {
  if (color === 'accent') {
    return getTextStyle(color)
  }

  return css`
    background-color: transparent;
    color: ${Color.White};

    &:not(:disabled) {
      &:hover {
        background-color: rgba(255, 255, 255, 0.16);
      }
    }
  `
}

export const getVariantStyle = (variant: Variant, color: ButtonColor = 'primary'): SerializedStyles =>
  ({
    filled: getFilledStyle(color),
    tonal: getTonalStyle(color),
    outlined: getOutlinedStyle(color),
    text: getTextStyle(color),
    onColor: getOnColorStyle(color),
    textOnColor: getTextOnColorStyle(color),
  })[variant]
